import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Grid,
  Divider,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DownloadIcon from "@mui/icons-material/Download";
import { useParams, useNavigate } from "react-router-dom";
import JSZip from "jszip";
import scriptRunnerV2Service from "../../services/scriptRunnerV2.service";
import useScriptPermissions from "./hooks/useScriptPermissions";

// interns scriptrunner - Import newly created components
import DynamicFormV2 from "./components/DynamicFormV2";
import ManualParametersV2 from "./components/ManualParametersV2";
import FilePreviewV2 from "./components/FilePreviewV2";
import DownloadSelectionDialog from "./components/DownloadSelectionDialog";

// import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
// import RefreshIcon from "@mui/icons-material/Refresh";

// interns scriptrunner - Main component for executing a script with file preview (V2)
const ScriptExecuteV2 = () => {
  const { scriptId, version } = useParams();
  const navigate = useNavigate();
  const { canRead, canWrite } = useScriptPermissions();

  // interns scriptrunner - redirect if no read permission
  useEffect(() => {
    if (!canRead) {
      navigate("/script-runner-V2");
    }
  }, [canRead, navigate]);

  const [script, setScript] = useState(null);
  const [versionData, setVersionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [formData, setFormData] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // interns scriptrunner - Added state for file previews during execution
  const [allFiles, setAllFiles] = useState({});
  const [selectedFile, setSelectedFile] = useState("");
  const [extractingFiles, setExtractingFiles] = useState(false);

  // interns scriptrunner - added execution tracking states
  const [executionId, setExecutionId] = useState(null);
  const [currentExecution, setCurrentExecution] = useState(null);

  // interns scriptrunner - State for selective download dialog
  const [downloadDialog, setDownloadDialog] = useState({
    open: false,
    executionId: null,
  });

  // ============================================================
  // gsd-py-runner - Added state to store filtered logs from S3 after completion - "Start"
  // ============================================================
  const [finalLogs, setFinalLogs] = useState([]);
  // ============================================================
  // gsd-py-runner - Added state to store filtered logs from S3 after completion - "End"
  // ============================================================

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const scriptData = await scriptRunnerV2Service.getScriptById(scriptId);
      setScript(scriptData);

      const versions = await scriptRunnerV2Service.getScriptVersions(scriptId);
      const currentVersion = versions.find((v) => v.version === version);

      if (!currentVersion) {
        throw new Error("Version metadata not found");
      }

      setVersionData(currentVersion);

      // interns scriptrunner - initialize form defaults from DB-stored inputSchema (Hybrid Approach)
      const schema = currentVersion.inputSchema;
      if (schema?.properties || schema?.fields) {
        const defaults = {};
        const fields =
          schema.fields ||
          (schema.properties
            ? Object.entries(schema.properties).map(([k, v]) => ({
                id: k,
                ...v,
              }))
            : []);

        fields.forEach((f) => {
          if (f.default !== undefined) {
            defaults[f.id || f.name] = f.default;
          }
        });
        setFormData(defaults);
        console.log("interns scriptrunner - Initialized form from DB schema");
      }

      // interns scriptrunner - fetch script files for preview panel (decoupled from form)
      loadScriptFiles(scriptId, version);

      setError(null);
    } catch (err) {
      console.error("interns scriptrunner - Failed to fetch script data", err);
      setError("Failed to load script details.");
    } finally {
      setLoading(false);
    }
  }, [scriptId, version]);

  // ============================================================
  // gsd-py-runner - Modified polling useEffect to capture final logs from S3 with retry - "Start"
  // (Added setFinalLogs from data.output.logs, and if empty, retry via getExecutionLogs after 2 seconds)
  // ============================================================
  useEffect(() => {
    let interval;
    let retryTimeout;
    if (
      executionId &&
      (currentExecution?.status === "running" ||
        currentExecution?.status === "paused")
    ) {
      interval = setInterval(async () => {
        try {
          const data =
            await scriptRunnerV2Service.getExecutionById(executionId);
          setCurrentExecution(data);

          // interns scriptrunner - if finished, update result and stop executing
          if (["completed", "failed", "terminated"].includes(data.status)) {
            setExecuting(false);
            setResult(data.output);
            clearInterval(interval);

            // gsd-py-runner - first try to use logs attached by backend
            const initialLogs = data.output?.logs || [];
            if (initialLogs.length > 0) {
              setFinalLogs(initialLogs);
            } else {
              // gsd-py-runner - if no logs yet (S3 upload delayed), retry after 2 seconds
              retryTimeout = setTimeout(async () => {
                try {
                  const logsData =
                    await scriptRunnerV2Service.getExecutionLogs(executionId);
                  if (logsData.logs && logsData.logs.length > 0) {
                    setFinalLogs(logsData.logs);
                  } else {
                    // Still no logs – show fallback message
                    setFinalLogs([]);
                  }
                } catch (err) {
                  console.error("Failed to fetch logs on retry", err);
                  setFinalLogs([]);
                }
              }, 2000);
            }
          }
        } catch (err) {
          console.error("interns scriptrunner - Polling error", err);
        }
      }, 3000);
    }
    return () => {
      clearInterval(interval);
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [executionId, currentExecution]);
  // ============================================================
  // gsd-py-runner - Modified polling useEffect - "End"
  // ============================================================

  // interns scriptrunner - extraction purely for the code viewer panel
  const loadScriptFiles = async (id, ver) => {
    try {
      setExtractingFiles(true);
      const zipBuffer = await scriptRunnerV2Service.downloadZip(id, ver);
      const zip = await JSZip.loadAsync(zipBuffer);

      const filesObj = {};
      const promises = [];

      zip.forEach((relativePath, file) => {
        if (!file.dir) {
          const promise = file.async("string").then((content) => {
            filesObj[relativePath] = content;
          });
          promises.push(promise);
        }
      });

      await Promise.all(promises);
      setAllFiles(filesObj);

      // interns scriptrunner - default selection logic for preview panel
      if (filesObj["main.py"]) setSelectedFile("main.py");
      else if (filesObj["template.json"]) setSelectedFile("template.json");
      else if (Object.keys(filesObj).length > 0)
        setSelectedFile(Object.keys(filesObj)[0]);
    } catch (err) {
      console.error(
        "interns scriptrunner - Failed to load files for preview",
        err,
      );
    } finally {
      setExtractingFiles(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFormChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleExecute = async () => {
    try {
      console.log("interns scriptrunner - [UI] Run Script clicked");
      console.log("interns scriptrunner - [UI] Script ID:", scriptId);
      console.log("interns scriptrunner - [UI] Version:", version);
      console.log("interns scriptrunner - [UI] Form Data:", formData);

      setExecuting(true);
      setResult(null);
      setExecutionId(null);
      setCurrentExecution(null);
      // ============================================================
      // gsd-py-runner - Reset finalLogs on new execution - "Start"
      // ============================================================
      setFinalLogs([]);
      // ============================================================
      // gsd-py-runner - Reset finalLogs on new execution - "End"
      // ============================================================

      const response = await scriptRunnerV2Service.executeScript(
        scriptId,
        version,
        formData,
      );

      console.log(
        "interns scriptrunner - [UI] Execution response received:",
        response,
      );
      setExecutionId(response.executionId);
      setCurrentExecution(response);

      setSnackbar({
        open: true,
        message: "Execution started!",
        severity: "success",
      });
    } catch (err) {
      console.error(
        "interns scriptrunner - [EXECUTION FAILED] Error details:",
        err,
      );
      setSnackbar({
        open: true,
        message: `Execution failed: ${err.response?.data?.message || err.message}`,
        severity: "error",
      });
      setExecuting(false);
    }
  };

  // interns scriptrunner - added control handlers
  // const handlePause = async () => {
  //   try {
  //     await scriptRunnerV2Service.pauseExecution(executionId);
  //     setSnackbar({
  //       open: true,
  //       message: "Pausing execution...",
  //       severity: "info",
  //     });
  //     const data = await scriptRunnerV2Service.getExecutionById(executionId);
  //     setCurrentExecution(data);
  //   } catch (err) {
  //     setSnackbar({
  //       open: true,
  //       message: "Failed to pause",
  //       severity: "error",
  //     });
  //   }
  // };

  // const handleResume = async () => {
  //   try {
  //     await scriptRunnerV2Service.resumeExecution(executionId);
  //     setSnackbar({
  //       open: true,
  //       message: "Resuming execution...",
  //       severity: "info",
  //     });
  //     const data = await scriptRunnerV2Service.getExecutionById(executionId);
  //     setCurrentExecution(data);
  //   } catch (err) {
  //     setSnackbar({
  //       open: true,
  //       message: "Failed to resume",
  //       severity: "error",
  //     });
  //   }
  // };

  const handleTerminate = async () => {
    try {
      await scriptRunnerV2Service.terminateExecution(executionId);
      setSnackbar({
        open: true,
        message: "Terminating execution...",
        severity: "warning",
      });
      const data = await scriptRunnerV2Service.getExecutionById(executionId);
      setCurrentExecution(data);
      setExecuting(false);
      setResult(data.output);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to terminate",
        severity: "error",
      });
    }
  };

  const handleDownload = () => {
    setDownloadDialog({ open: true, executionId });
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );

  if (error || !script)
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error || "Script not found"}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/script-runner-V2/view")}
          sx={{
            mt: 2,
            color: "#28BCBB",
            backgroundColor: "#f0f9f9",
            border: "1px solid rgba(40, 188, 187, 0.3)",
            boxShadow: "0 2px 6px rgba(40, 188, 187, 0.15)",
            borderRadius: "8px",
            padding: "6px 14px",
            fontWeight: 600,
            textTransform: "none",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#e6f0f1",
              borderColor: "#188182",
              boxShadow: "0 4px 12px rgba(40, 188, 187, 0.25)",
            },
          }}
        >
          Back to Scripts
        </Button>
      </Box>
    );

  const getStatusColor = (status) => {
    switch (status) {
      case "running":
        return "primary";
      case "paused":
        return "warning";
      case "completed":
        return "success";
      case "failed":
        return "error";
      case "terminated":
        return "error";
      default:
        return "default";
    }
  };

  // ============================================================
  // gsd-py-runner - Added displayLogsContent function with priority logic - "Start"
  // ============================================================
  const displayLogsContent = () => {
    if (executing) {
      return (
        <Typography variant="caption" sx={{ color: "#aaa" }}>
          ⏳ Execution in progress – logs will appear after completion.
        </Typography>
      );
    }
    if (finalLogs.length > 0) {
      return finalLogs.map((log, idx) => (
        <Typography
          key={idx}
          variant="caption"
          display="block"
          sx={{ fontSize: "0.7rem", whiteSpace: "pre-wrap" }}
        >
          {log}
        </Typography>
      ));
    }
    const logsArray = result?.logs || currentExecution?.output?.logs || [];
    if (logsArray.length > 0) {
      return logsArray.map((log, idx) => (
        <Typography
          key={idx}
          variant="caption"
          display="block"
          sx={{ fontSize: "0.7rem" }}
        >
          {log}
        </Typography>
      ));
    }
    if (
      result &&
      result.result &&
      result.result.message === "Execution finished successfully"
    ) {
      return (
        <Typography variant="caption" sx={{ color: "#aaa" }}>
          Script completed – no logs available.
        </Typography>
      );
    }
    return (
      <Typography variant="caption" sx={{ color: "#aaa" }}>
        No logs yet. Run the script to see output.
      </Typography>
    );
  };
  // ============================================================
  // gsd-py-runner - Added displayLogsContent function - "End"
  // ============================================================

  return (
    <Box sx={{ p: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/script-runner-V2/view")}
        sx={{
          mb: 3,
          color: "#28BCBB",
          backgroundColor: "#f0f9f9",
          border: "1px solid rgba(40, 188, 187, 0.3)",
          boxShadow: "0 2px 6px rgba(40, 188, 187, 0.15)",
          borderRadius: "8px",
          padding: "6px 14px",
          fontWeight: 600,
          textTransform: "none",
          transition: "all 0.3s ease",
          "&:hover": {
            backgroundColor: "#e6f0f1",
            borderColor: "#188182",
            boxShadow: "0 4px 12px rgba(40, 188, 187, 0.25)",
          },
        }}
      >
        Back to Scripts
      </Button>

      <Grid container spacing={3}>
        {/* interns scriptrunner - Left column for Inputs and Execution Results */}
        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }} elevation={2}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#1d9f9f" }}
            >
              Run: {script.name}
            </Typography>
            <Typography
              component="div"
              variant="body2"
              color="textSecondary"
              sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              Version:{" "}
              <Chip
                label={version}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {script.description}
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#1d9f9f" }}
            >
              Configuration
            </Typography>

            {versionData?.inputSchema?.properties ||
            versionData?.inputSchema?.fields ? (
              <DynamicFormV2
                schema={versionData.inputSchema}
                onChange={handleFormChange}
                values={formData}
                disabled={executing}
              />
            ) : (
              <ManualParametersV2
                onChange={(manualData) => setFormData(manualData)}
                disabled={executing}
              />
            )}

            <Box sx={{ mt: 3 }}>
              {!executing && !currentExecution ? (
                canWrite && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={handleExecute}
                    startIcon={<PlayArrowIcon />}
                    sx={{ py: 1.5, fontWeight: "bold", background: "#1d9f9f" }}
                  >
                    Run Script
                  </Button>
                )
              ) : (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", color: "#1d9f9f" }}
                    >
                      Status:
                    </Typography>
                    <Chip
                      label={
                        currentExecution?.status?.toUpperCase() ||
                        "INITIALIZING"
                      }
                      color={getStatusColor(currentExecution?.status)}
                      size="small"
                    />
                    {executing && <CircularProgress size={16} />}
                  </Box>

                  {/* interns scriptrunner - tracking Kubernetes pod details for script execution */}
                  {currentExecution?.podName && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        bgcolor: "#f5f5f5",
                        borderRadius: 1,
                        border: "1px solid #ddd",
                      }}
                    >
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        Kubernetes Pod:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                      >
                        {currentExecution.podName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Namespace: {currentExecution.namespace || "default"}
                      </Typography>
                    </Box>
                  )}

                  {/* interns scriptrunner - execution control buttons */}
                  <Grid container spacing={1}>
                    {/* {currentExecution?.status === "running" && canWrite && (
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="warning"
                          onClick={handlePause}
                          startIcon={<PauseIcon />}
                        >
                          Pause
                        </Button>
                      </Grid>
                    )}
                    {currentExecution?.status === "paused" && canWrite && (
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="primary"
                          onClick={handleResume}
                          startIcon={<RefreshIcon />}
                        >
                          Resume
                        </Button>
                      </Grid>
                    )} */}
                    {(currentExecution?.status === "running" ||
                      currentExecution?.status === "paused") &&
                      canWrite && (
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            onClick={handleTerminate}
                            startIcon={<StopIcon />}
                          >
                            Terminate
                          </Button>
                        </Grid>
                      )}
                    {["completed", "failed", "terminated"].includes(
                      currentExecution?.status,
                    ) && (
                      <>
                        {canWrite && (
                          <Grid item xs={6}>
                            <Button
                              fullWidth
                              variant="contained"
                              color="primary"
                              onClick={handleExecute}
                              startIcon={<PlayArrowIcon />}
                            >
                              Run Again
                            </Button>
                          </Grid>
                        )}
                        <Grid item xs={canWrite ? 6 : 12}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="success"
                            onClick={handleDownload}
                            startIcon={<DownloadIcon />}
                          >
                            Download
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              )}
            </Box>
          </Paper>

          {/* interns scriptrunner - Execution status and logs shown below form */}
          {(result || currentExecution) && (
            <Box>
              <Card
                sx={{
                  mb: 2,
                  bgcolor: "#f0f4f8",
                  borderLeft: `5px solid ${currentExecution?.status === "completed" ? "#4caf50" : "#1976d2"}`,
                }}
              >
                <CardContent sx={{ py: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#1976d2", fontWeight: "bold" }}
                  >
                    EXECUTION ID: {executionId}
                  </Typography>
                </CardContent>
              </Card>

              {result?.result && (
                <>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Output Data
                  </Typography>
                  <Paper
                    sx={{ p: 2, bgcolor: "#f8f9fa", mb: 2, borderRadius: 2 }}
                  >
                    <pre
                      style={{
                        margin: 0,
                        fontSize: "0.75rem",
                        overflow: "auto",
                      }}
                    >
                      {JSON.stringify(result.result, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}

              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                Execution Logs
              </Typography>
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: "#000",
                  color: "#0f0",
                  fontFamily: "monospace",
                  maxHeight: 300,
                  overflow: "auto",
                  borderRadius: 2,
                }}
              >
                {/* ============================================================ */}
                {/* gsd-py-runner - Replaced inline mapping with displayLogsContent() - "Start" */}
                {/* ============================================================ */}
                {displayLogsContent()}
                {/* ============================================================ */}
                {/* gsd-py-runner - Replaced inline mapping with displayLogsContent() - "End" */}
                {/* ============================================================ */}
              </Paper>
            </Box>
          )}
        </Grid>

        {/* interns scriptrunner - Right column for File Content Preview panel */}
        <Grid item xs={12} lg={7}>
          <Box sx={{ position: "sticky", top: 20 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: "bold",
                color: "#1d9f9f",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Script Content Preview
              {extractingFiles && <CircularProgress size={16} />}
            </Typography>

            <FilePreviewV2
              files={allFiles}
              selectedFile={selectedFile}
              onFileChange={setSelectedFile}
            />

            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 1, display: "block", fontStyle: "italic" }}
            >
              {/*interns scriptrunner - viewing file contents locally extracted
              from ZIP*/}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Download Selection Dialog */}
      <DownloadSelectionDialog
        open={downloadDialog.open}
        handleClose={() =>
          setDownloadDialog({ open: false, executionId: null })
        }
        executionId={downloadDialog.executionId}
      />
    </Box>
  );
};

export default ScriptExecuteV2;
